import type { NeedCategory, NeedRecurrence, NeedUrgency } from '@/lib/types'
import { ageBucketFor, type AgeBucket } from '@/lib/rhythm/blocks'

/**
 * Onboarding-wizard template engine.
 *
 * Given the parent's 3 answers + baby's age bucket, returns a set of
 * recurring Need cards to auto-seed. The parent lands on /needs with
 * their week already scaffolded — no more staring at an empty list.
 *
 * This is the MOAT: no competitor pre-fills the calendar. Nara Baby,
 * Huckleberry, Baby Tracker all leave you a blank form. Wren asks 3
 * questions and comes back with ~6-10 ready-to-tap-when-they-happen
 * items you can override or delete.
 */

export type FeedingMethod = 'breast' | 'bottle' | 'mixed' | 'solids'
export type SleepStyle    = 'own_crib' | 'room_share' | 'bedshare'
export type CareCoverage  = 'me_home' | 'partner_home' | 'daycare_mf' | 'nanny' | 'other'

export interface OnboardingAnswers {
  feeding: FeedingMethod
  sleep: SleepStyle
  care: CareCoverage
}

export interface NeedSeed {
  title: string
  description?: string
  category: NeedCategory
  urgency: NeedUrgency
  recurrence: NeedRecurrence | null
  /** Time-of-day 24h clock the seed uses to compute `due_at`. */
  dueTimeOfDay?: string
  /** Alternatively: offset in hours from onboarding time. */
  dueOffsetHours?: number
  /** Optional person target. Left null = family-wide. */
  personId?: string | null
}

/** Compute a concrete due_at ISO for a seed, relative to `now`. */
export function seedDueAt(seed: NeedSeed, now = new Date()): string | null {
  if (seed.dueTimeOfDay) {
    const [h, m] = seed.dueTimeOfDay.split(':').map(Number)
    const d = new Date(now)
    d.setHours(h, m, 0, 0)
    if (d.getTime() <= now.getTime()) d.setDate(d.getDate() + 1)
    return d.toISOString()
  }
  if (seed.dueOffsetHours != null) return new Date(now.getTime() + seed.dueOffsetHours * 3_600_000).toISOString()
  return null
}

export function seedNeedsFromAnswers(
  answers: OnboardingAnswers,
  ageMonths: number | null,
  focusedPersonId: string | null,
): NeedSeed[] {
  const seeds: NeedSeed[] = []
  const bucket: AgeBucket | null = ageBucketFor(ageMonths ?? -1)

  // ── Feeding-driven seeds ─────────────────────────────────────────────
  if (answers.feeding === 'bottle' || answers.feeding === 'mixed') {
    seeds.push({
      title: 'Sanitize bottles',
      description: 'Weekly deep-clean cycle keeps bottles safe for infant use.',
      category: 'maintenance',
      urgency: 'normal',
      recurrence: 'weekly',
      dueTimeOfDay: '21:00',
      personId: focusedPersonId,
    })
    seeds.push({
      title: 'Restock formula',
      description: 'Refill before the current tin drops below 1/3.',
      category: 'equipment',
      urgency: 'normal',
      recurrence: 'weekly',
      dueTimeOfDay: '10:00',
      personId: focusedPersonId,
    })
  }
  if (answers.feeding === 'breast' || answers.feeding === 'mixed') {
    seeds.push({
      title: 'Prep pump parts',
      description: 'Wash + air-dry pump parts every night for tomorrow.',
      category: 'maintenance',
      urgency: 'normal',
      recurrence: 'daily',
      dueTimeOfDay: '21:30',
      personId: focusedPersonId,
    })
  }
  if (answers.feeding === 'solids' || (bucket === '6-9mo' || bucket === '9-12mo')) {
    seeds.push({
      title: 'Prep solids for the week',
      description: 'Batch-cook + portion purees or finger foods for the coming week.',
      category: 'feeding',
      urgency: 'normal',
      recurrence: 'weekly',
      dueTimeOfDay: '15:00',
      personId: focusedPersonId,
    })
  }

  // ── Sleep-driven seeds ───────────────────────────────────────────────
  seeds.push({
    title: 'Bedtime routine',
    description: bucket === '0-3mo'
      ? 'Dim lights, feed, swaddle, sleep. Consistency helps circadian rhythm settle.'
      : 'Bath → book → bottle/nurse → bed. Same order builds a strong sleep cue.',
    category: 'activity',
    urgency: 'normal',
    recurrence: 'daily',
    dueTimeOfDay: bucket === '0-3mo' ? '20:00' : '19:00',
    personId: focusedPersonId,
  })
  if (answers.sleep === 'own_crib' || answers.sleep === 'room_share') {
    seeds.push({
      title: 'Change crib sheets',
      description: 'Weekly rotation. Two sets so one is always fresh.',
      category: 'maintenance',
      urgency: 'low',
      recurrence: 'weekly',
      dueTimeOfDay: '11:00',
      personId: focusedPersonId,
    })
  }

  // ── Care-coverage seeds ──────────────────────────────────────────────
  if (answers.care === 'daycare_mf') {
    seeds.push({
      title: 'Pack daycare bag',
      description: 'Bottles, diapers, spare outfit, weather layer, comfort item.',
      category: 'logistics',
      urgency: 'high',
      recurrence: 'weekdays',
      dueTimeOfDay: '07:00',
      personId: focusedPersonId,
    })
    seeds.push({
      title: 'Daycare pickup',
      category: 'logistics',
      urgency: 'high',
      recurrence: 'weekdays',
      dueTimeOfDay: '17:00',
      personId: focusedPersonId,
    })
  }
  if (answers.care === 'nanny') {
    seeds.push({
      title: 'Nanny handoff',
      description: 'Quick briefing: last feed, next nap, anything unusual.',
      category: 'logistics',
      urgency: 'normal',
      recurrence: 'weekdays',
      dueTimeOfDay: '08:30',
      personId: focusedPersonId,
    })
  }
  if (answers.care === 'partner_home') {
    seeds.push({
      title: 'Evening handoff',
      description: 'Share the day: feeds, moods, medications, tomorrow plan.',
      category: 'logistics',
      urgency: 'normal',
      recurrence: 'daily',
      dueTimeOfDay: '18:00',
      personId: focusedPersonId,
    })
  }

  // ── Universal seeds (every family gets these) ────────────────────────
  seeds.push({
    title: 'Weekly grocery',
    description: 'Formula, diapers, wipes, family food.',
    category: 'logistics',
    urgency: 'normal',
    recurrence: 'weekly',
    dueTimeOfDay: '10:00',
    // Family-wide, no person.
    personId: null,
  })
  seeds.push({
    title: 'Book next pediatric visit',
    description: 'Schedule the next well-baby check based on your pediatrician\'s cadence.',
    category: 'appointment',
    urgency: 'high',
    recurrence: null, // one-shot
    dueOffsetHours: 30 * 24,
    personId: focusedPersonId,
  })

  return seeds
}

/** Short preview of what the wizard will seed. Used in the confirmation card before the parent hits "Fill my week". */
export function seedPreview(answers: OnboardingAnswers, ageMonths: number | null): { total: number; daily: number; weekdays: number; weekly: number; oneShot: number } {
  const seeds = seedNeedsFromAnswers(answers, ageMonths, null)
  return {
    total: seeds.length,
    daily: seeds.filter((s) => s.recurrence === 'daily').length,
    weekdays: seeds.filter((s) => s.recurrence === 'weekdays').length,
    weekly: seeds.filter((s) => s.recurrence === 'weekly').length,
    oneShot: seeds.filter((s) => s.recurrence == null).length,
  }
}
